import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './YoutubeVideos.css';

const YoutubeVideos = () => {
  const {t} = useTranslation();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.');
  const envApiUrl = process.env.REACT_APP_API_URL?.trim();
  const API_URL = envApiUrl && (isLocalHost || (!envApiUrl.includes("localhost") && !envApiUrl.includes("127.0.0.1"))) ? envApiUrl : '/api';

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await fetch(`${API_URL}/youtube-videos`, {
          headers: {
          },
        });

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const contentType = res.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          const json = await res.json();

          if (json.success && Array.isArray(json.data)) {
            setVideos(json.data);
          }
        } else {
          throw new Error('Response was not JSON');
        }
      } catch (error) {
        console.error('Failed to fetch youtube videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [API_URL]);

  const getYoutubeId = (url) => {
    if (!url) return null;

    let videoId = null;

    // If user just pasted the 11 character ID
    if (/^[a-zA-Z0-9_-]{11}$/.test(url.trim())) {
      return url.trim();
    }

    try {
      if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split(/[?#]/)[0];
      } else {
        const urlObj = new URL(url);

        if (urlObj.hostname.includes('youtube.com')) {
          if (urlObj.pathname.startsWith('/shorts/')) {
            videoId = urlObj.pathname.split('/shorts/')[1]?.split(/[?#]/)[0];
          } else if (urlObj.pathname.startsWith('/embed/')) {
            videoId = urlObj.pathname.split('/embed/')[1]?.split(/[?#]/)[0];
          } else {
            videoId = urlObj.searchParams.get('v');
          }
        }
      }
    } catch (e) {
      // Ignore URL parsing errors
    }

    if (!videoId) {
      const regExp =
         /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([^"&?/\s]{11})/i;

      const match = url.match(regExp);
      videoId = match ? match[1] : null;
    }

    return videoId && videoId.length === 11 ? videoId : null;
  };

  if (loading || videos.length === 0) {
    return null;
  }

  return (
    <section className="youtube-videos-section">
      <div className="container">
        <div className="section-header">
          <span className="badge-tag green">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginRight: '5px' }}
            >
              <polygon points="23 7 16 12 23 17 23 7"></polygon>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
            Videos
          </span>

          <h2 className="section-title">
              {t('youtube.sectionTitle', 'See Our Equipment in Action')}
          </h2>

          <p className="section-subtitle">
          {t(
               'youtube.sectionSubtitle',
               'Watch demonstrations and testimonials from our happy farmers'
            )}
          </p>
        </div>

        {(() => {
          const featuredVideo = videos.find(v => v.isFeatured);
          if (!featuredVideo) return null;
          const videoId = getYoutubeId(featuredVideo.url);
          if (!videoId) return null;
          return (
            <div className="featured-video-container" style={{ marginBottom: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div className="featured-video-wrapper" style={{ position: 'relative', width: '100%', maxWidth: '960px', paddingBottom: '54%', height: 0, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}`}
                  title={featuredVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; compute-pressure"
                  allowFullScreen
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                />
              </div>
              <h3 className="featured-video-title" style={{ marginTop: '20px', fontSize: '24px', fontWeight: '800', textAlign: 'center', color: 'var(--text-primary)' }}>{featuredVideo.title}</h3>
            </div>
          );
        })()}

        <div className="videos-grid">
          {videos.filter(v => !v.isFeatured).map((video, index) => {
            const videoId = getYoutubeId(video.url);

            if (!videoId) return null;

            return (
              <div key={video._id || index} className="video-card">
                <div className="video-wrapper">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; compute-pressure"
                    allowFullScreen
                  />
                </div>

                <h3 className="video-title">{video.title}</h3>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default YoutubeVideos;

